import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api_editEventType, api_getSubTypes } from '../../../api/catering/eventType'; // Adjusted API import for editing and fetching event types and sub types
import { toast } from 'react-hot-toast';
import { myToastWarning } from '../../../helpers/myToast';

type SubType = {
	_id: string;
	name: string;
	description: string;
};

type EventType = {
	_id: string;
	name: string;
	description: string;
	type: string;
	subEventType: SubType[];
};

const EditEventType = () => {
	const eventType: EventType = useLocation().state;
	const [loading, setLoading] = useState<boolean>(false);
	const [name, setName] = useState<string>(eventType?.name || '');
	const [description, setDescription] = useState<string>(eventType?.description || '');
	const [selectedSubTypes, setSelectedSubTypes] = useState<SubType[]>(eventType?.subEventType || []);
	const [subTypes, setSubTypes] = useState<SubType[]>([]); // State to hold fetched subtypes
	const navigate = useNavigate();

	useEffect(() => {
		fetchSubTypes();
	}, []);

	const fetchSubTypes = async () => {
		try {
			const res = await api_getSubTypes();
			if (res.isSuccess) {
				const uniqueSubTypes = res.data.filter((subType: SubType, index: number, self: SubType[]) =>
					index === self.findIndex((t) => t.name === subType.name)
				);
				setSubTypes(uniqueSubTypes); // Update state with unique subtypes
			} else {
				toast.error('Failed to fetch sub types');
			}
		} catch (error) {
			console.error('Error fetching sub types:', error);
			toast.error('Failed to fetch sub types');
		}
	};

	const handleSubTypeClick = (subType: SubType) => {
		if (selectedSubTypes.some((item) => item._id === subType._id)) {
			setSelectedSubTypes(selectedSubTypes.filter((item) => item._id !== subType._id));
		} else {
			setSelectedSubTypes([...selectedSubTypes, subType]);
		}
	};

	const editEventType = async () => {
		if (!name) {
			myToastWarning('Please Enter Name');
			return;
		}
		try {
			setLoading(true);
			const payload = {
				name,
				description,
				subEventType: selectedSubTypes.map((subType) => subType._id),
			};

			const res = await api_editEventType(payload, eventType._id);
			const message = typeof res.message === 'string' ? res.message : 'Success';
			if (res.isSuccess) {
				toast.success(message || 'Event type updated successfully');
				setTimeout(() => {
					navigate(-1);
				}, 100);
			} else {
				toast.error(message || 'Failed to update event type');
			}
		} catch (error) {
			console.error('Error editing event type:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, padding: '40px' }}>
			<div className='mb-3'>
				<h3>Edit Event Type</h3>
			</div>

			<div className='mb-3'>
				<label htmlFor='name' className='form-label'>
					Name
				</label>
				<input
					id='name'
					value={name}
					onChange={(e) => setName(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					placeholder='Enter name'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='description' className='form-label'>
					Description
				</label>
				<input
					id='description'
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					placeholder='Enter description'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='subTypes' className='form-label'>
					Select Sub Types
				</label>
				<div style={{ display: 'flex', flexWrap: 'wrap', width: '400px' }}>
					{subTypes.map((item) => (
						<button
							key={item._id}
							onClick={() => handleSubTypeClick(item)}
							style={{ margin: '5px', padding: '10px', cursor: 'pointer' }}
							className={`btn ${selectedSubTypes.some((sub) => sub._id === item._id) ? 'btn-secondary' : 'btn-light'}`}
						>
							{item.name}
						</button>
					))}
				</div>
			</div>

			<div className='mb-3'>
				<label className='form-label'>Selected Sub Types:</label>
				<ul>
					{selectedSubTypes.map((subType) => (
						<li key={subType._id}>
							{subType.name} - {subType.description}
						</li>
					))}
				</ul>
			</div>

			<button onClick={editEventType} type='button' className='btn btn-lg btn-primary' disabled={loading}>
				{loading ? 'Loading..' : 'Submit'}
			</button>
		</div>
	);
};

export default EditEventType;
